import { useMemo } from 'react';
import * as LucideIcons from 'lucide-react';
import { InitialsAvatar } from '../../shared';
import { TableColumn, StatusTag } from '../../shared/table';
import { GroupMember } from '../../../types/groupDetail';

interface UseParticipantTableConfigProps {
  isAllSelected: boolean;
  handleSelectAll: (checked: boolean) => void;
  isMemberSelected: (member: GroupMember) => boolean;
  handleSelectMember: (member: GroupMember, checked: boolean) => void;
}

export const useParticipantTableConfig = ({
  isAllSelected,
  handleSelectAll,
  isMemberSelected,
  handleSelectMember,
}: UseParticipantTableConfigProps): TableColumn<GroupMember>[] => {
  return useMemo(() => [
    // Checkbox column
    {
      header: (
        <input
          type="checkbox"
          checked={isAllSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="cursor-pointer w-4 h-4"
        />
      ),
      width: '40px',
      align: 'center' as const,
      renderCell: (member: GroupMember) => (
        <input
          type="checkbox"
          checked={isMemberSelected(member)}
          onChange={(e) => handleSelectMember(member, e.target.checked)}
          className="cursor-pointer w-4 h-4"
        />
      ),
    },
    // Name column with avatar
    {
      header: 'Name',
      width: '250px',
      flexGrow: true,
      renderCell: (member: GroupMember) => {
        return (
          <div className="flex items-center">
            <div className="mr-3">
              <InitialsAvatar 
                personId={member.id} 
                name={member.name}
                avatarUrl={member.avatarUrl}
              />
            </div>
            <span className="text-sm">{member.name}</span>
          </div>
        );
      },
    },
    // Role column
    {
      header: 'Role',
      width: '400px',
      renderCell: (member: GroupMember) => {
        const roleVariantMap = {
          'Group Leader': 'info-orange' as const,
          'Assistant Leader': 'info' as const,
          'Member': 'default' as const,
        };
        
        return (
          <StatusTag 
            label={member.role} 
            variant={roleVariantMap[member.role]}
          />
        );
      },
    },
    // Communication Settings column
    {
      header: 'Communication Settings',
      width: '300px',
      renderCell: (member: GroupMember) => {
        const { communication } = member;
        return (
          <div className="flex items-center gap-2">
            <div className="relative group" title="Receives Messages">
              <LucideIcons.MailCheck 
                className={`w-4 h-4 ${communication.receivesMessages ? 'text-green-600' : 'text-gray-300'}`} 
              />
            </div>
            <div className="relative group" title="Receives Comments">
              <LucideIcons.MessageCircle 
                className={`w-4 h-4 ${communication.receivesComments ? 'text-green-600' : 'text-gray-300'}`} 
              />
            </div>
            <div className="relative group" title="Receives Weekly Summary">
              <LucideIcons.Calendar 
                className={`w-4 h-4 ${communication.receivesWeeklySummary ? 'text-green-600' : 'text-gray-300'}`} 
              />
            </div>
            <div className="relative group" title="Receives Text">
              <LucideIcons.Smartphone 
                className={`w-4 h-4 ${communication.receivesText ? 'text-green-600' : 'text-gray-300'}`} 
              />
            </div>
            {communication.unsubscribed && (
              <div className="relative group" title="Unsubscribed">
                <LucideIcons.MailX className="w-4 h-4 text-red-600" />
              </div>
            )}
            {communication.noEmailAddress && (
              <div className="relative group" title="No Email Address">
                <LucideIcons.MailQuestion className="w-4 h-4 text-yellow-600" />
              </div>
            )}
            {communication.ownCommSettings && (
              <div className="relative group" title="Own Communication Settings">
                <LucideIcons.UserCog className="w-4 h-4 text-yellow-600" />
              </div>
            )}
          </div>
        );
      },
    },
  ], [isAllSelected, handleSelectAll, isMemberSelected, handleSelectMember]);
};
